import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { guestsQuery } from '@/lib/guests';
import { inputCls, labelCls } from './formStyles';
import type { Guest } from '@/api/generated/types.gen';

// Guest search-or-create for the manual-booking form.

export type GuestMode = 'search' | 'create';

export interface GuestFieldProps {
  mode: GuestMode;
  onModeChange: (m: GuestMode) => void;
  selectedGuest: Guest | null;
  onSelectGuest: (g: Guest | null) => void;
  newName: string;
  newEmail: string;
  newPhone: string;
  onNewNameChange: (v: string) => void;
  onNewEmailChange: (v: string) => void;
  onNewPhoneChange: (v: string) => void;
}

export function GuestField({
  mode,
  onModeChange,
  selectedGuest,
  onSelectGuest,
  newName,
  newEmail,
  newPhone,
  onNewNameChange,
  onNewEmailChange,
  onNewPhoneChange,
}: GuestFieldProps) {
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQ(q), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [q]);

  const { data: results } = useQuery({
    ...guestsQuery(debouncedQ, 10),
    enabled: mode === 'search' && debouncedQ.trim().length >= 1,
  });

  const items = results?.items ?? [];

  if (mode === 'create') {
    return (
      <div className="space-y-3">
        <div>
          <label htmlFor="nbf-guest-name" className={labelCls}>
            Guest name *
          </label>
          <input
            id="nbf-guest-name"
            type="text"
            required
            value={newName}
            onChange={(e) => onNewNameChange(e.target.value)}
            className={inputCls}
            placeholder="Full name"
          />
        </div>
        <div>
          <label htmlFor="nbf-guest-email" className={labelCls}>
            Email
          </label>
          <input
            id="nbf-guest-email"
            type="email"
            value={newEmail}
            onChange={(e) => onNewEmailChange(e.target.value)}
            className={inputCls}
            placeholder="guest@example.com"
          />
        </div>
        <div>
          <label htmlFor="nbf-guest-phone" className={labelCls}>
            Phone
          </label>
          <input
            id="nbf-guest-phone"
            type="tel"
            value={newPhone}
            onChange={(e) => onNewPhoneChange(e.target.value)}
            className={inputCls}
            placeholder="+381 …"
          />
        </div>
        <button
          type="button"
          className="text-xs text-primary hover:underline"
          onClick={() => onModeChange('search')}
        >
          ← Search existing guest
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      {selectedGuest ? (
        <div className="flex items-center gap-2 rounded-[var(--radius-control)] border border-border bg-input px-3 py-2 text-sm">
          <span className="flex-1 font-medium">{selectedGuest.full_name}</span>
          {selectedGuest.email && (
            <span className="text-muted text-xs">{selectedGuest.email}</span>
          )}
          <button
            type="button"
            aria-label="Clear guest"
            className="text-muted hover:text-foreground"
            onClick={() => {
              onSelectGuest(null);
              setQ('');
              setShowDropdown(false);
            }}
          >
            ×
          </button>
        </div>
      ) : (
        <>
          <input
            id="nbf-guest-search"
            type="text"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setShowDropdown(true);
            }}
            onFocus={() => setShowDropdown(true)}
            onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
            className={inputCls}
            placeholder="Search by name or email…"
            aria-label="Search guest"
          />
          {showDropdown && items.length > 0 && (
            <ul
              role="listbox"
              className="absolute z-10 mt-1 w-full rounded-[var(--radius-control)] border border-border bg-card shadow-md"
            >
              {items.map((g) => (
                <li
                  key={g.id}
                  role="option"
                  aria-selected={false}
                  className="cursor-pointer px-3 py-2 text-sm hover:bg-[var(--surface-sunken)]"
                  onMouseDown={() => {
                    onSelectGuest(g);
                    setQ('');
                    setShowDropdown(false);
                  }}
                >
                  <span className="font-medium">{g.full_name}</span>
                  {g.email && (
                    <span className="ml-2 text-xs text-muted">{g.email}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      <button
        type="button"
        className="mt-2 text-xs text-primary hover:underline"
        onClick={() => onModeChange('create')}
      >
        + Create new guest
      </button>
    </div>
  );
}
